import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <>
      <Helmet>
        <title>Hvem er vi | Tømrerfirmaet RNS på Djursland</title>
        <meta name="description" content="Læs om Tømrerfirmaet RNS. Vi er et erfarent og kvalitetsbevidst tømrerfirma i Glatved ved Balle, der udfører tømrer-, snedker- og malerarbejde på hele Djursland." />
        <meta property="og:title" content="Hvem er vi | Tømrerfirmaet RNS på Djursland" />
        <meta property="og:description" content="Læs om Tømrerfirmaet RNS. Vi er et erfarent og kvalitetsbevidst tømrerfirma i Glatved ved Balle, der udfører tømrer-, snedker- og malerarbejde på hele Djursland." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/image00125.jpeg" />
        <link rel="canonical" href="https://rns.dk/om-os" />
        <html lang="da-DK" />
      </Helmet>



      {/* Main Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Text Column */}
          <div className="lg:col-span-7 space-y-6">
            <h2 className="font-display font-extrabold text-3xl text-slate-900">
              Vi er et kvalitetsbevidst tømrerfirma
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Det er vigtigt for os, at vores arbejde lever op til dine forventninger. Vi holder os derfor løbende opdateret på markedets nyeste materialer og udstyr og sikrer, at vores arbejde er kvalitetsbevidst.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Vi bestræber os på at kombinere det traditionelle håndværk med moderne teknikker – og er behjælpelige på såvel almindelige som mere komplicerede projekter.
            </p>

            <div className="pt-4 space-y-3">
              <h3 className="font-display font-bold text-xl text-slate-900">
                Som veletableret tømrer står vi til rådighed ved enhver tømreropgave:
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-black" />
                  Traditionelle tømreropgaver
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-black" />
                  Renovering af butik og kontor
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-black" />
                  Udarbejdelse af butiksinventar
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-black" />
                  Snedkerarbejde
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-black" />
                  Sommerhusrenovering
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-black" />
                  Malerarbejde
                </li>
              </ul>
            </div>

            <p className="text-slate-600 leading-relaxed pt-2">
              Vores arbejde tager altid udgangspunkt i dit behov, og vi skræddersyr hver enkelt opgave efter dine ønsker.
            </p>

            <div className="pt-4">
              <a
                href="tel:22439147"
                className="bg-black hover:bg-black/90 text-white font-bold py-2.5 px-5 text-sm sm:py-3 sm:px-8 sm:text-base rounded-none transition-all inline-flex items-center gap-2 shadow-md"
              >
                <i className="fa-solid fa-phone text-white" />
                +45 22 43 91 47
              </a>
            </div>
          </div>

          {/* Image Column */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/image00076.jpeg"
                alt="Tømrerarbejde og tagrenovering udført af Tømrerfirmaet RNS på Djursland"
                className="w-full h-48 object-cover rounded-none border border-slate-100 shadow-sm"
              />
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/image00090.jpeg"
                alt="Solidt træhåndværk og montering udført af faguddannet tømrer"
                className="w-full h-64 object-cover rounded-none border border-slate-100 shadow-sm"
              />
            </div>
            <div className="pt-8">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/image00125.jpeg"
                alt="Kvalitetsbevidst tømrer- og snedkerarbejde til bolig og sommerhus på Djursland"
                className="w-full h-96 object-cover rounded-none border border-slate-100 shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-slate-950 text-white border-t border-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <h2 className="font-display font-extrabold text-3xl text-white">
            Har du spørgsmål til vores arbejde?
          </h2>
          <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto">
            Eller vil du have et uforpligtende tilbud på din næste tømreropgave? Vi ser frem til din henvendelse og står klar til at rådgive dig professionelt.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold py-2.5 px-5 text-sm sm:py-3.5 sm:px-8 sm:text-base rounded-none transition-all shadow-sm"
            >
              Kontakt os her
            </Link>
            <a
              href="tel:22439147"
              className="bg-white hover:bg-slate-50 text-black border border-black font-semibold py-2.5 px-5 text-sm sm:py-3.5 sm:px-8 sm:text-base rounded-none transition-all shadow-sm inline-flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone text-black" aria-hidden="true"></i>
              +45 22 43 91 47
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
